import { memo, useCallback, useState, type JSX } from 'react'

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { pick } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { useColumns } from 'domains/MediaPlatforms/Pages'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { mediaLibraryConfigRoute } from 'pages/NWPlatform/MediaLibraries/MediaLibrary'
import { mediaPlatformPagesOverviewRoute } from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Pages'
import { mediaAssetQueryKeys } from 'services/queryKeys/media'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const [newDialogOpen, setNewDialogOpen] = useState(false)

  const navigate = useNavigate({ from: mediaPlatformPagesOverviewRoute.fullPath })
  const { environmentId, platformId } = mediaPlatformPagesOverviewRoute.useParams()
  const { query } = mediaPlatformPagesOverviewRoute.useSearch()

  const pagesQuery = useInfiniteQuery({
    ...mediaAssetQueryKeys.pageList({
      environmentId,
      platformId,
      query,
    }),
    select: (data) => ({ flat: data.pages.flatMap((page) => page.items) }),
  })
  const pages = pagesQuery.data?.flat

  const columns = useColumns()

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:MediaPlatform.pages.searchPages')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(
            () => queryClient.invalidateQueries({ queryKey: mediaAssetQueryKeys.pageList({ environmentId }).queryKey }),

            [environmentId, queryClient],
          )}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        size={56}
        data={pages ?? []}
        columns={columns}
        isInitialLoadingGlobal={pagesQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={mediaPlatformPagesOverviewRoute.fullPath}
            // todo replace with page config route
            to={mediaLibraryConfigRoute.to}
            params={{ mediaLibraryId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id']}
        enableInfinity
        infinityProps={pick(pagesQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        // ActionMenuProps={(row) => ({
        //   title: row.original.name,
        //   entity: t('domain:MediaPlatform.mediaPlatform'),
        // })}
        // RowActions={(props) => <RowActions {...props} />}
      />

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        {/* <NewDialog /> */}
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
